import { UnitOfMeasure } from '@usf/mpp-types';
import { UnitOfMeasureLabels, UnitType } from '../enum/unit-conversion-enums';
import { UnitConversionHelper } from './unit-conversion-helper';
import { IngredientUnitsCalculations } from '@app/mpp/model/recipe-detail-view-model';
import { UOM } from '../enum/mpp-enums';

export class PortionCalculatorHelper {
  public static calculateBaseMeasurement = (
    yieldValue: number,
    yieldUOM: UnitOfMeasureLabels,
    existingMeasures: UnitOfMeasure,
  ) => {
    const baseUnitType = UnitConversionHelper.getUnitType(yieldUOM);
    switch (baseUnitType) {
      case UnitType.SOLID:
        return UnitConversionHelper.convertAndUpdateSolidMeasures(
          yieldValue,
          yieldUOM,
          existingMeasures,
        );
      case UnitType.LIQUID:
        return UnitConversionHelper.convertAndUpdateLiquidMeasures(
          yieldValue,
          yieldUOM,
          existingMeasures,
        );
      case UnitType.PIECE:
        return UnitConversionHelper.setPieces(yieldValue, existingMeasures);
    }
  };

  public static runUnitsOfMeasureRecalculations = (
    newValue: number,
    newMeasurement: string,
    oldValue: number,
    oldMeasurement: string,
    measurements: UnitOfMeasure,
  ) => {
    let newUnitMeasure = UnitConversionHelper.transformUOMToUnitOfMeasureLabel(
      newMeasurement as UOM,
    );
    let ratioForAdjustments = 1;

    if (newValue !== oldValue) {
      ratioForAdjustments = newValue / oldValue;
    }

    if (newMeasurement !== oldMeasurement) {
      const oldMeasurementForNewUOM = this.getValueForMeasurement(
        measurements,
        newUnitMeasure,
      );
      if (!oldMeasurementForNewUOM) {
        ratioForAdjustments = 1;
      } else {
        ratioForAdjustments = newValue / oldMeasurementForNewUOM;
      }
    }
    let newType = UnitConversionHelper.getUnitType(newUnitMeasure);
    let finalMeasurements = { ...measurements };

    if (ratioForAdjustments !== 1) {
      switch (newType) {
        case UnitType.SOLID:
          finalMeasurements = UnitConversionHelper.applyLiquidMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          finalMeasurements = UnitConversionHelper.applyPieceMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          break;
        case UnitType.LIQUID:
          finalMeasurements = UnitConversionHelper.applySolidMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          finalMeasurements = UnitConversionHelper.applyPieceMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          break;
        case UnitType.PIECE:
          finalMeasurements = UnitConversionHelper.applyLiquidMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          finalMeasurements = UnitConversionHelper.applySolidMeasureRatio(
            finalMeasurements,
            ratioForAdjustments,
          );
          break;
      }
    }

    return this.calculateBaseMeasurement(
      newValue,
      newUnitMeasure,
      finalMeasurements,
    );
  };

  // For to Solid and from Pieces conversion, we can pass in any value for netweight.
  public static runUnitsOfMeasureCalculations = (
    netWeight: number,
    calculations: IngredientUnitsCalculations,
    measurements: UnitOfMeasure,
  ) => {
    const baseUnitType = UnitConversionHelper.getUnitType(
      calculations.baseUnit as UnitOfMeasureLabels,
    );
    const conversionUnitType = UnitConversionHelper.getUnitType(
      calculations.conversionUnit as UnitOfMeasureLabels,
    );

    let calculatedMeasurements;

    switch (conversionUnitType) {
      case UnitType.SOLID:
        if (baseUnitType === UnitType.LIQUID) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculateSolidPortionsByLiquid(
              measurements?.cup,
              calculations.baseQuantity,
              calculations.baseUnit as UnitOfMeasureLabels,
              calculations.conversionQunatity,
              calculations.conversionUnit as UnitOfMeasureLabels,
              measurements,
            );
        }

        if (baseUnitType === UnitType.PIECE) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculateSolidPortionsByPieces(
              calculations.baseQuantity,
              calculations.conversionQunatity,
              calculations.conversionUnit as UnitOfMeasureLabels,
              measurements,
            );
        }

        break;
      case UnitType.LIQUID:
        if (baseUnitType === UnitType.SOLID) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculateLiquidPortionsByWeight(
              netWeight,
              calculations.baseQuantity,
              calculations.conversionQunatity,
              calculations.baseUnit as UnitOfMeasureLabels,
              calculations.conversionUnit as UnitOfMeasureLabels,
              measurements,
            );
        }

        if (baseUnitType === UnitType.PIECE) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculateLiquidPortionsByPieces(
              calculations.baseQuantity,
              calculations.totalPieces,
              calculations.conversionQunatity,
              calculations.conversionUnit as UnitOfMeasureLabels,
              measurements,
            );
        }

        break;
      case UnitType.PIECE:
        if (baseUnitType === UnitType.SOLID) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculatePiecesBySolid(
              netWeight,
              calculations.baseQuantity,
              calculations.conversionQunatity,
              calculations.baseUnit as UnitOfMeasureLabels,
              measurements,
            );
        }

        if (baseUnitType === UnitType.LIQUID) {
          calculatedMeasurements =
            PortionCalculatorHelper.calculatePiecesByLiquid(
              calculations.totalCups,
              calculations.baseQuantity,
              calculations.conversionQunatity,
              calculations.baseUnit as UnitOfMeasureLabels,
              measurements,
            );
        }
        break;
    }

    return calculatedMeasurements;
  };

  public static calculateSolidPortionsByPieces(
    pieces: number,
    conversionWeight: number,
    conversionUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ): UnitOfMeasure {
    const calculateWeight =
      UnitConversionHelper.convertToLB(conversionWeight, conversionUnit) /
      pieces;
    return UnitConversionHelper.convertAndUpdateSolidMeasures(
      calculateWeight,
      conversionUnit,
      existingMeasures,
    );
  }

  public static calculateSolidPortionsByLiquid(
    totalCups: number,
    baseWeight: number,
    baseUnit: UnitOfMeasureLabels,
    conversionWeight: number,
    conversionUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ): UnitOfMeasure {
    const baseByCup = UnitConversionHelper.convertToCup(baseWeight, baseUnit);
    const adjustedCups = totalCups / baseByCup;
    const baseByLBWeight = UnitConversionHelper.convertToLB(
      conversionWeight,
      conversionUnit,
    );
    const adjustWeightByValue = adjustedCups / baseByLBWeight;
    return UnitConversionHelper.convertAndUpdateLiquidMeasures(
      adjustWeightByValue,
      conversionUnit,
      existingMeasures,
    );
  }

  public static calculateLiquidPortionsByWeight(
    netWeight: number,
    baseWeight: number,
    conversionWeight: number,
    baseUnit: UnitOfMeasureLabels,
    conversionUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ): UnitOfMeasure {
    const baseByLBWeight = UnitConversionHelper.convertToLB(
      baseWeight,
      baseUnit,
    );
    const adjustWeightByValue = netWeight / baseByLBWeight;
    const calculatedQuantity = conversionWeight * adjustWeightByValue;
    return UnitConversionHelper.convertAndUpdateLiquidMeasures(
      calculatedQuantity,
      conversionUnit,
      existingMeasures,
    );
  }

  public static calculateLiquidPortionsByPieces(
    baseQuantity: number,
    totalQuantity: number,
    conversionQuantity: number,
    conversionUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ) {
    const fractionOfPieces = totalQuantity / baseQuantity;
    const adjustedLiquidWeightByFractionOfPieces =
      conversionQuantity * fractionOfPieces;
    return UnitConversionHelper.convertAndUpdateLiquidMeasures(
      adjustedLiquidWeightByFractionOfPieces,
      conversionUnit,
      existingMeasures,
    );
  }

  public static calculatePiecesByLiquid(
    totalCups: number,
    baseWeight: number,
    pieces: number,
    baseUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ) {
    const baseByCup = UnitConversionHelper.convertToCup(baseWeight, baseUnit);
    const adjustedCups = totalCups / baseByCup;
    return UnitConversionHelper.setPieces(
      pieces * adjustedCups,
      existingMeasures,
    );
  }

  public static calculatePiecesBySolid(
    netWeight: number,
    baseWeight: number,
    pieces: number,
    baseUnit: UnitOfMeasureLabels,
    existingMeasures: Partial<UnitOfMeasure> = {},
  ) {
    const baseByLBWeight = UnitConversionHelper.convertToLB(
      baseWeight,
      baseUnit,
    );
    const adjustWeightByValue = netWeight / baseByLBWeight;
    return UnitConversionHelper.setPieces(
      pieces * adjustWeightByValue,
      existingMeasures,
    );
  }

  public static isValidPortionCalculation(
    unitQuantity: number,
    portionQuantity: number,
    unitType: UnitType,
  ): boolean {
    if (unitType === UnitType.PIECE) {
      return (
        Number.isInteger(unitQuantity) &&
        Number.isInteger(portionQuantity) &&
        unitQuantity > 0 &&
        portionQuantity > 0 &&
        portionQuantity <= unitQuantity &&
        Number.isFinite(unitQuantity / portionQuantity)
      );
    }

    return (
      unitQuantity > 0 &&
      portionQuantity > 0 &&
      portionQuantity <= unitQuantity &&
      Number.isFinite(unitQuantity / portionQuantity)
    );
  }

  public static getValueForMeasurement(
    units: UnitOfMeasure,
    selectedUnit: UnitOfMeasureLabels,
  ) {
    switch (selectedUnit) {
      case UnitOfMeasureLabels.POUND:
        return units?.pound;
      case UnitOfMeasureLabels.OUNCE:
        return units?.ounce;
      case UnitOfMeasureLabels.GRAMS:
        return units?.grams;
      case UnitOfMeasureLabels.CUP:
        return units?.cup;
      case UnitOfMeasureLabels.GALLON:
        return units?.gallon;
      case UnitOfMeasureLabels.QUART:
        return units?.quart;
      case UnitOfMeasureLabels.PINT:
        return units?.pint;
      case UnitOfMeasureLabels.LIQUID_OUNCE:
        return units?.liquidOunce;
      case UnitOfMeasureLabels.TABLESPOON:
        return units?.tablespoon;
      case UnitOfMeasureLabels.TEASPOON:
        return units?.teaspoon;
      case UnitOfMeasureLabels.LITER:
        return units?.liters;
      case UnitOfMeasureLabels.MILLILITER:
        return units?.liters * 1000;
      case UnitOfMeasureLabels.PIECE:
        return units?.portion;
      case UnitOfMeasureLabels.EACH:
        return units?.portion;
      case UnitOfMeasureLabels.SLICE:
        return units?.slice;
      case UnitOfMeasureLabels.CUP_WEIGHT:
        return units?.cupWeight;
      case UnitOfMeasureLabels.SCOOP_6:
        return units?.scoop6;
      case UnitOfMeasureLabels.SCOOP_8:
        return units?.scoop8;
      case UnitOfMeasureLabels.SCOOP_10:
        return units?.scoop10;
      case UnitOfMeasureLabels.SCOOP_12:
        return units?.scoop12;
      case UnitOfMeasureLabels.SCOOP_16:
        return units?.scoop16;
      case UnitOfMeasureLabels.SCOOP_20:
        return units?.scoop20;
      case UnitOfMeasureLabels.SCOOP_24:
        return units?.scoop24;
      case UnitOfMeasureLabels.SCOOP_30:
        return units?.scoop30;
      case UnitOfMeasureLabels.SCOOP_40:
        return units?.scoop40;
      case UnitOfMeasureLabels.SCOOP_50:
        return units?.scoop50;
      case UnitOfMeasureLabels.SCOOP_60:
        return units?.scoop60;
      case UnitOfMeasureLabels.SCOOP_70:
        return units?.scoop70;
      case UnitOfMeasureLabels.SCOOP_100:
        return units?.scoop100;
      case UnitOfMeasureLabels.LADLE_1_OZ:
        return units?.ladle1;
      case UnitOfMeasureLabels.LADLE_2_OZ:
        return units?.ladle2;
      case UnitOfMeasureLabels.LADLE_3_OZ:
        return units?.ladle3;
      case UnitOfMeasureLabels.LADLE_4_OZ:
        return units?.ladle4;
      case UnitOfMeasureLabels.LADLE_5_OZ:
        return units?.ladle5;
      case UnitOfMeasureLabels.LADLE_6_OZ:
        return units?.ladle6;
      case UnitOfMeasureLabels.LADLE_8_OZ:
        return units?.ladle8;
      case UnitOfMeasureLabels.LADLE_12_OZ:
        return units?.ladle12;
      default:
        return undefined;
    }
  }
}
